import { type CSSProperties } from "react";
import { SC } from "./studioChrome";

// Shared workspace/tab model for the operator consoles (ADR 0123 → 0126). A "workspace"
// is a tab: a named, ordered, hideable descriptor that (from Phase 2, ADR 0124) also
// owns a widget `layout` + `widgets` set. Built-in workspaces reproduce today's tabs;
// custom workspaces (ADR 0125) are operator-created. Persisted per-surface in the
// profile (ADR 0120). This module holds the pure model + the ⚙ Tabs editor; the canvas
// lives in WorkspaceCanvas.tsx.

/** One placed widget's grid position (react-grid-layout item shape). */
export interface WidgetLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

/** A tab. `builtin` reproduces a hard-coded console tab; `!builtin` is operator-created
 *  (ADR 0125). `widgets` (ids placed on this tab) + `layout` (their positions) arrive in
 *  Phase 2 (ADR 0124); optional here so Phase 1 profiles round-trip forward. */
export interface WorkspaceDef {
  id: string;
  name: string;
  builtin: boolean;
  hidden: boolean;
  widgets?: string[];
  layout?: WidgetLayout[];
}

export const HOME_WORKSPACE_ID = "console";

/** Reconcile a saved workspace list against the current built-in registry (ADR 0123):
 *  keep the saved order + saved name/hidden/widgets/layout for known tabs, keep custom
 *  tabs, drop unknown built-in ids (removed in a newer build), append built-ins the save
 *  predates, and force the home (Console) tab present + visible so you can't lock
 *  yourself out. Pure — safe to call on every load. */
export function reconcileWorkspaces(
  saved: WorkspaceDef[] | undefined,
  builtins: WorkspaceDef[],
): WorkspaceDef[] {
  const byId = new Map(builtins.map((b) => [b.id, b]));
  const out: WorkspaceDef[] = [];
  const seen = new Set<string>();
  for (const w of saved ?? []) {
    if (!w || typeof w.id !== "string" || seen.has(w.id)) continue;
    const b = byId.get(w.id);
    if (b) {
      out.push({ ...b, ...w, builtin: true, name: w.name || b.name });
      seen.add(w.id);
    } else if (w.builtin === false) {
      out.push({ ...w, builtin: false, name: w.name || w.id });
      seen.add(w.id);
    }
    // else: an unknown built-in-flagged id from an older build → drop it
  }
  for (const b of builtins) {
    if (!seen.has(b.id)) {
      out.push({ ...b });
      seen.add(b.id);
    }
  }
  const home = out.find((w) => w.id === HOME_WORKSPACE_ID);
  if (home) home.hidden = false;
  else out.unshift(byId.get(HOME_WORKSPACE_ID) ?? { id: HOME_WORKSPACE_ID, name: "Console", builtin: true, hidden: false });
  return out;
}

export const visibleWorkspaces = (ws: WorkspaceDef[]): WorkspaceDef[] => ws.filter((w) => !w.hidden);

// ── widget placement (ADR 0124) ────────────────────────────────────────────────────
// Each widget is a SINGLETON: placed on exactly one workspace. The profile stores, per
// workspace, `widgets` (ids) + `layout` (their grid positions).

export interface WorkspaceWidgetDefaults {
  [workspaceId: string]: { widgets: string[]; layout: WidgetLayout[] };
}

/** Seed default widgets/layout into any workspace that has none yet — first run, or a
 *  built-in re-added by a newer build (ADR 0124). Workspaces that already have `widgets`
 *  (incl. an empty custom tab) are left alone, so a saved arrangement is never clobbered. */
export function seedWidgetDefaults(ws: WorkspaceDef[], defaults: WorkspaceWidgetDefaults): WorkspaceDef[] {
  return ws.map((w) => {
    if (w.widgets !== undefined) return w;
    const d = defaults[w.id];
    return d
      ? { ...w, widgets: [...d.widgets], layout: d.layout.map((l) => ({ ...l })) }
      : { ...w, widgets: [], layout: [] };
  });
}

export const allPlacedWidgetIds = (ws: WorkspaceDef[]): Set<string> => {
  const s = new Set<string>();
  for (const w of ws) for (const id of w.widgets ?? []) s.add(id);
  return s;
};

/** Drop widget ids / layout items not in the known registry, and de-dup a widget that
 *  somehow landed on two tabs (ADR 0124) — a stale save can never reference a removed
 *  widget or double-place one. */
export function validateWidgetPlacement(ws: WorkspaceDef[], knownIds: Set<string>): WorkspaceDef[] {
  const seen = new Set<string>();
  return ws.map((w) => {
    const widgets: string[] = [];
    for (const id of w.widgets ?? []) {
      if (knownIds.has(id) && !seen.has(id)) {
        widgets.push(id);
        seen.add(id);
      }
    }
    const layout = (w.layout ?? []).filter((l) => widgets.includes(l.i));
    return { ...w, widgets, layout };
  });
}

/** Move a widget onto `targetId` (removing it from any other workspace — singleton),
 *  appending a layout item at the bottom of the target. No-op if already there. */
export function placeWidget(
  ws: WorkspaceDef[],
  widgetId: string,
  targetId: string,
  size: { w: number; h: number; minW?: number; minH?: number },
): WorkspaceDef[] {
  return ws.map((w) => {
    if (w.id === targetId) {
      if ((w.widgets ?? []).includes(widgetId)) return w;
      const maxY = (w.layout ?? []).reduce((m, l) => Math.max(m, l.y + l.h), 0);
      return {
        ...w,
        widgets: [...(w.widgets ?? []), widgetId],
        layout: [...(w.layout ?? []), { i: widgetId, x: 0, y: maxY, w: size.w, h: size.h, minW: size.minW, minH: size.minH }],
      };
    }
    if ((w.widgets ?? []).includes(widgetId)) {
      return { ...w, widgets: (w.widgets ?? []).filter((id) => id !== widgetId), layout: (w.layout ?? []).filter((l) => l.i !== widgetId) };
    }
    return w;
  });
}

/** Remove a widget from wherever it's placed → back to the toolbox. */
export function removeWidget(ws: WorkspaceDef[], widgetId: string): WorkspaceDef[] {
  return ws.map((w) =>
    (w.widgets ?? []).includes(widgetId)
      ? { ...w, widgets: (w.widgets ?? []).filter((id) => id !== widgetId), layout: (w.layout ?? []).filter((l) => l.i !== widgetId) }
      : w,
  );
}

/** Sanitize an RGL onLayoutChange payload to our stored shape (drop moved/static/etc.). */
export const sanitizeLayout = (l: { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number }[]): WidgetLayout[] =>
  l.map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH }));

export const layoutEqual = (a: WidgetLayout[], b: WidgetLayout[]): boolean => {
  if (a.length !== b.length) return false;
  const key = (l: WidgetLayout) => `${l.i}:${l.x},${l.y},${l.w},${l.h}`;
  const bs = new Set(b.map(key));
  return a.every((l) => bs.has(key(l)));
};

export function setWorkspaceLayout(ws: WorkspaceDef[], targetId: string, layout: WidgetLayout[]): WorkspaceDef[] {
  return ws.map((w) => (w.id === targetId ? { ...w, layout } : w));
}

/** Move item at `index` by `dir` (-1 up / +1 down), returning a new array. */
export function moveWorkspace(ws: WorkspaceDef[], index: number, dir: -1 | 1): WorkspaceDef[] {
  const j = index + dir;
  if (index < 0 || index >= ws.length || j < 0 || j >= ws.length) return ws;
  const copy = ws.slice();
  [copy[index], copy[j]] = [copy[j], copy[index]];
  return copy;
}

// ── ⚙ Tabs editor ────────────────────────────────────────────────────────────────
// Show/hide + reorder (ADR 0123); create/rename/delete custom tabs + reset (ADR 0125).
// The home (Console) tab is locked visible. Callers pass `onCreate`/`onRename`/`onDelete`/
// `onReset` to enable the Phase 3 controls; omit to hide them (Phase 1).

const rowBtn: CSSProperties = {
  padding: "3px 8px",
  borderRadius: 7,
  border: `1px solid ${SC.hair3}`,
  background: SC.chipBg,
  color: SC.dim,
  fontFamily: SC.mono,
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer",
};

export function WorkspaceTabsEditor({
  workspaces,
  onChange,
  onClose,
  onCreate,
  onReset,
}: {
  workspaces: WorkspaceDef[];
  onChange: (ws: WorkspaceDef[]) => void;
  onClose: () => void;
  onCreate?: () => void;
  onReset?: () => void;
}) {
  const setHidden = (id: string, hidden: boolean) =>
    onChange(workspaces.map((w) => (w.id === id ? { ...w, hidden } : w)));
  const rename = (id: string, name: string) =>
    onChange(workspaces.map((w) => (w.id === id ? { ...w, name } : w)));
  const del = (id: string) => onChange(workspaces.filter((w) => w.id !== id));

  return (
    <div role="dialog" aria-label="Tabs settings"
      style={{ position: "absolute", top: 74, right: 8, zIndex: 40, width: 300, maxHeight: "70vh", overflow: "auto", background: SC.panel, border: `1px solid ${SC.hair}`, borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", padding: 11, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim }}>TABS</div>
        <button type="button" aria-label="Close tabs settings" onClick={onClose} style={{ ...rowBtn, border: "none", background: "none" }}>✕</button>
      </div>
      <div style={{ fontSize: 10, color: SC.dim, lineHeight: 1.4 }}>Show/hide and reorder your tabs. Console can't be hidden.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {workspaces.map((w, i) => {
          const locked = w.id === HOME_WORKSPACE_ID;
          return (
            <div key={w.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 7px", borderRadius: 8, background: SC.chipBg, border: `1px solid ${SC.hair}` }}>
              <input type="checkbox" aria-label={`Show ${w.name} tab`} checked={!w.hidden} disabled={locked} onChange={(e) => setHidden(w.id, !e.target.checked)} style={{ accentColor: SC.blue }} />
              {w.builtin ? (
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: SC.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{w.name}</span>
              ) : (
                <input aria-label={`Rename ${w.name} tab`} value={w.name} onChange={(e) => rename(w.id, e.target.value)}
                  style={{ flex: 1, minWidth: 0, padding: "3px 6px", borderRadius: 6, border: `1px solid ${SC.hair2}`, background: SC.meterBg, color: SC.text, fontFamily: SC.sans, fontSize: 12 }} />
              )}
              <button type="button" aria-label={`Move ${w.name} up`} disabled={i === 0} onClick={() => onChange(moveWorkspace(workspaces, i, -1))} style={{ ...rowBtn, opacity: i === 0 ? 0.35 : 1 }}>↑</button>
              <button type="button" aria-label={`Move ${w.name} down`} disabled={i === workspaces.length - 1} onClick={() => onChange(moveWorkspace(workspaces, i, 1))} style={{ ...rowBtn, opacity: i === workspaces.length - 1 ? 0.35 : 1 }}>↓</button>
              {!w.builtin && (
                <button type="button" aria-label={`Delete ${w.name} tab`} onClick={() => del(w.id)} style={{ ...rowBtn, color: SC.redSoft, borderColor: "rgba(255,69,58,0.4)" }}>✕</button>
              )}
            </div>
          );
        })}
      </div>
      {(onCreate || onReset) && (
        <div style={{ display: "flex", gap: 8, paddingTop: 4, borderTop: `1px solid ${SC.divider}` }}>
          {onCreate && <button type="button" aria-label="New tab" onClick={onCreate} style={{ ...rowBtn, color: SC.blueHi, borderColor: SC.blueEdge }}>＋ New tab</button>}
          <div style={{ flex: 1 }} />
          {onReset && <button type="button" aria-label="Reset all tabs" onClick={onReset} style={{ ...rowBtn }}>Reset all</button>}
        </div>
      )}
    </div>
  );
}
